import React from "react";
import queryString from "query-string";
import cc from "classcat";

import { Lockup } from "./components/lockup";
import { ConfirmationStep } from "./components/confirmation-step";
import { InitialStep } from "./components/initial-step";
import { SuccessStep } from "./components/success-step";
import { useConfirmCode, useContainerSize, useSubmitUserInfo } from "./hooks";
import { useOptInStore } from "./store";
import {
  ConfirmationFormValues,
  DisplayType,
  UserInfoFormikValues,
  WorkflowStep,
} from "./types";
import { hexToRgb } from "./lib";
import { DEFAULT_BODY, DEFAULT_TITLE } from "./lib/constants";

function App() {
  const size = useContainerSize();

  const parsed = queryString.parse(location.search);
  const { display = "modal" as DisplayType } = parsed;
  const domain = parsed.domain as string;
  const name = parsed.name as string;
  const tos_url = parsed.tos_url as string;
  const policy_url = parsed.policy_url as string;
  const user_id = parsed.user_id as string;
  const phone = String(parsed.phone);
  const optin_method = String(display);
  const primary_button_text_color = String(
    parsed.primary_button_text_color || "#ffffff"
  );
  const colorRgb = hexToRgb(String(parsed.color));
  const color = colorRgb ? `${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}` : null;
  const font_family = String(parsed.font_family || "");
  const font_weight = String(parsed.font_weight || "");
  const font_css_url = String(parsed.font_css_url || "");
  const font_style = String(parsed.font_style || "");
  const button_preference = parsed.button_preference || "rounded";
  const show_discount_text = parsed.show_discount_text === "true";
  const discount_text = String(parsed.discount_text);
  const body = String(parsed.body || DEFAULT_BODY);
  const title = String(parsed.title || DEFAULT_TITLE);
  const terms_of_service = String(parsed.terms_of_service || "");
  const show_repeat_logo = parsed.show_repeat_logo === "true";

  color && document.documentElement.style.setProperty("--brand-color", color);

  document.documentElement.style.setProperty(
    "--brand-cta-text",
    primary_button_text_color
  );

  font_family !== "" &&
    document.documentElement.style.setProperty(
      `--brand-font-family`,
      `"${font_family}"`
    );

  font_weight !== "" &&
    document.documentElement.style.setProperty(
      `--brand-font-weight`,
      `${font_weight}`
    );

  font_style !== "" &&
    document.documentElement.style.setProperty(
      `--brand-font-style`,
      `${font_style}`
    );

  const borderRadius = button_preference === "rounded" ? "0.25rem" : "0px";
  const borderRadiusSmall =
    button_preference === "rounded" ? "0.125rem" : "0px";

  document.documentElement.style.setProperty(
    `--brand-border-style`,
    `${borderRadius}`
  );

  document.documentElement.style.setProperty(
    `--brand-border-style-sm`,
    `${borderRadiusSmall}`
  );

  const stylesheetLink = document.querySelector(`link[href="${font_css_url}"]`);

  if (!stylesheetLink && font_css_url !== "") {
    const newLink = document.createElement("link");
    newLink.href = font_css_url;
    newLink.rel = "stylesheet";
    document.head.appendChild(newLink);
  }

  const { step, setStep, setSubmissionError, setConfirmationError } =
    useOptInStore();

  const { mutateAsync } = useSubmitUserInfo({
    onSuccess: () => {
      setStep(WorkflowStep.Confirm);
    },
    onError: (e) => {
      let message = (e as Error).message;
      setSubmissionError(message);
    },
  });

  const { mutateAsync: confirmMutateAsync } = useConfirmCode({
    onMutate: () => {
      // Clear the existing confirmation error, helpful for consecutive submits.
      setConfirmationError("");
    },
    onSuccess: () => {
      setStep(WorkflowStep.Success);
    },
    onError: () => {
      setConfirmationError(
        "The verification code you've entered is incorrect."
      );
    },
  });

  const handleSubmitUserInfo = async (values: UserInfoFormikValues) => {
    await mutateAsync({
      ...values,
      phone_number: values.phone || "",
      domain,
      user_id,
      optin_method,
    });
  };

  const handleConfirmation = async (values: ConfirmationFormValues) => {
    await confirmMutateAsync({
      ...values,
      user_id,
      optin_method,
      domain,
    });
  };

  const containerClass = cc([
    {
      "wrap-modal": display === "modal",
      "wrap-page": display === "page",
    },
  ]);

  const responsiveClass = cc([
    { "is-desktop": size === "desktop", "is-mobile": size === "mobile" },
  ]);

  return (
    <div data-display={display} className={responsiveClass}>
      <div className={containerClass}>
        {step === WorkflowStep.Initial && (
          <InitialStep
            discountText={discount_text}
            display={display as DisplayType}
            name={name}
            onSubmit={handleSubmitUserInfo}
            phone={phone}
            privacyUrl={policy_url}
            showDiscountText={show_discount_text}
            tosUrl={tos_url}
            title={title}
            body={body}
            termsOfService={terms_of_service}
          />
        )}
        {step === WorkflowStep.Confirm && (
          <ConfirmationStep onSubmit={handleConfirmation} />
        )}
        {step === WorkflowStep.Success && <SuccessStep />}
      </div>
      {show_repeat_logo && <Lockup />}
    </div>
  );
}

export default App;
